import { useEffect, useState } from 'react'

import { PermissionState } from '~types'

interface GeolocationPermissionResult {
  state: PermissionState
  canUserFix: boolean
}

// for checking a website's policy
interface DocumentWithPermissionsPolicy extends Document {
  permissionsPolicy?: {
    allowsFeature: (feature: string) => boolean
  }
  featurePolicy?: {
    allowsFeature: (feature: string) => boolean
  }
}

export function useGeolocationPermission(): GeolocationPermissionResult {
  const [state, setState] = useState<PermissionState>(PermissionState.LOADING)

  const statusToState = (permissionStatus: PermissionStatus): void => {
    if (permissionStatus.state === 'granted') {
      setState(PermissionState.BROWSER_GRANTED)
    } else if (permissionStatus.state === 'denied') {
      setState(PermissionState.BROWSER_DENIED)
    } else {
      setState(PermissionState.BROWSER_PROMPT)
    }
  }

  useEffect(() => {
    let permissionStatus: PermissionStatus | null = null

    const checkPermission = async (): Promise<(() => void) | undefined> => {
      if (!navigator.geolocation) {
        setState(PermissionState.UNKNOWN)
        return
      }

      const doc = document as DocumentWithPermissionsPolicy

      const policyAPI = doc.permissionsPolicy || doc.featurePolicy
      if (policyAPI && !policyAPI.allowsFeature('geolocation')) {
        setState(PermissionState.POLICY_BLOCKED)
        return
      }

      try {
        permissionStatus = await navigator.permissions.query({ name: 'geolocation' })
        statusToState(permissionStatus)

        const handleChange = (): void => {
          if (permissionStatus) {
            statusToState(permissionStatus)
          }
        }

        permissionStatus.addEventListener('change', handleChange)

        return (): void => {
          permissionStatus?.removeEventListener('change', handleChange)
        }
      } catch (_error: unknown) {
        setState(PermissionState.UNKNOWN)
        return
      }
    }

    const cleanup = checkPermission()

    return (): void => {
      cleanup?.then((cleanupFn) => cleanupFn?.())
    }
  }, [])

  const canUserFix = state !== PermissionState.POLICY_BLOCKED && state !== PermissionState.UNKNOWN

  return { state, canUserFix }
}

export default useGeolocationPermission
